/**
 * Project analysis cache — backed by Supabase project_cache table.
 *
 * Schema (from 001_initial.sql):
 *   project_name text, rootdata_id text, analysis jsonb,
 *   raw_data jsonb, structured_labels jsonb,
 *   scored_at timestamptz, expires_at timestamptz (24h TTL)
 *
 * RLS (after 002_cache_anon_policy.sql):
 *   SELECT: anyone  |  INSERT/UPDATE: anyone
 */
import { supabase } from '../lib/supabase';
import { RD_BASE } from './config';

/** Normalize project name for comparison (lowercase, strip non-alphanumeric). */
function normalizeName(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Escape % _ \ so ilike treats the input as literal text, not a LIKE pattern.
 * Use this for ALL case-insensitive project name lookups in Supabase queries.
 */
export function safeNameForIlike(s) {
  return String(s || '').replace(/[%_\\]/g, '\\$&');
}

/**
 * Public criterion: cache row is "displayable" (radar chart will render with
 * real scores, not a fake 0). Use this for BOTH:
 *   - getCached's bypass logic (don't bypass loadable cache for missing
 *     newer features — return data with _missingFeatures flag instead)
 *   - UI-side button visibility (fetchCachedScores in App.jsx + the inline
 *     signalMap query in TrendingPage). Both surfaces must agree, otherwise
 *     "查看" button shows but click triggers full re-analysis.
 *
 * Bypass list (DOES make cache non-displayable):
 *   - _warmed=true: background warmer wrote partial entry, no real scores
 *   - composite/scores missing: radar would render at 0
 *   - _lightweight=true: resolver fallback, no real data sources
 *
 * NOT in bypass list (loadable but feature-incomplete):
 *   - complianceRisk missing/error: ⚖️ card empty, but rest of UI renders
 *   - news_signals missing: 📰 card empty, but rest of UI renders
 *   These now return data + _missingFeatures so the UI can show a banner
 *   prompting user to click 🔄 if they want the latest features.
 */
export function cacheIsDisplayable(row) {
  if (!row) return false;
  const analysis = row.analysis || {};
  const raw = row.raw_data || {};
  if (analysis._warmed === true) return false;
  if (analysis.composite == null || analysis.scores == null) return false;
  if (raw.details?._lightweight === true) return false;
  return true;
}

/**
 * Inspect raw_data for missing newer features (compliance, news, etc.).
 * Returned list is attached to getCached output as `_missingFeatures` so the
 * UI can surface a "missing newer features, click 🔄 to refresh" banner.
 */
export function detectMissingFeatures(rawData, analysis) {
  const missing = [];
  if (!rawData) return missing;
  if (!('complianceRisk' in rawData)) missing.push('complianceRisk');
  else if (rawData.complianceRisk?._error) missing.push('complianceRisk_error');
  if (!analysis?.news_signals) missing.push('news_signals');
  return missing;
}

/**
 * Return cached entry (analysis + raw_data) if not expired, else null.
 * Validates that the cached project actually matches the query name — guards
 * against stale entries written by a previous buggy match (e.g. searching
 * "The Interfold" returning Ethereum data).
 */
export async function getCached(projectName, opts = {}) {
  if (!supabase || !projectName) return null;
  const { rootdataId = null } = (typeof opts === 'string') ? { rootdataId: opts } : (opts || {});
  try {
    // 如果调用方通过 autocomplete 锁定了 rootdata_id，优先按 (name, rootdata_id) 精确查
    // 避免：用户搜"Pharos Network"选中 RWA 公链，但缓存里有另一个"Pharos"导致拿错项目
    // 名字用 ilike 大小写不敏感匹配 — 用户输入 "uniswap" 也能命中以 "Uniswap" 写入的缓存。
    let query = supabase
      .from('project_cache')
      .select('analysis, raw_data, scored_at, rootdata_id')
      .ilike('project_name', safeNameForIlike(projectName))
      .gt('expires_at', new Date().toISOString())
      .order('scored_at', { ascending: false })
      .limit(1);
    if (rootdataId) query = query.eq('rootdata_id', String(rootdataId));
    const { data } = await query.maybeSingle();
    if (!data) return null;

    // 如果调用方指定了 rootdata_id 且缓存的 rootdata_id 不匹配，绝不命中
    if (rootdataId && String(data.rootdata_id || '') !== String(rootdataId)) {
      console.warn(`[cache] rootdata_id 不匹配: 选中=${rootdataId} 缓存=${data.rootdata_id}，跳过缓存`);
      return null;
    }

    // ── Name validation: cached details must match the query ──
    const qNorm = normalizeName(projectName);
    const cachedName = data.raw_data?.details?.project_name || "";
    const cachedNorm = normalizeName(cachedName);
    // 允许：完全相等 / 子串包含（如 "Kalshi" vs "Kalshi Markets"）
    const isMatch = qNorm === cachedNorm
      || (cachedNorm && (qNorm.includes(cachedNorm) || cachedNorm.includes(qNorm)));
    if (!isMatch) {
      console.warn(`[cache] 缓存名称不匹配: 查询="${projectName}" 缓存="${cachedName}"，跳过缓存`);
      // 异步删除坏缓存（不阻塞 UI）
      // 注意：只在 rootdata_id 未指定时删；指定 ID 时缓存可能给另一个项目复用，别误删
      if (!rootdataId) {
        supabase.from('project_cache')
          .delete()
          .ilike('project_name', safeNameForIlike(projectName))
          .then(() => console.log(`[cache] 已删除坏缓存: ${projectName}`));
      }
      return null;
    }

    // ── HARD bypass (cache literally not displayable) ──────────────────────
    //    Per cacheIsDisplayable: warmed/_lightweight/no-scores rows render an
    //    empty radar chart at 0. Bypass these and force full analysis.
    if (!cacheIsDisplayable(data)) {
      const reasons = [];
      if (data.analysis?._warmed === true) reasons.push('warmed');
      if (data.analysis?.composite == null || data.analysis?.scores == null) reasons.push('no_scores');
      if (data.raw_data?.details?._lightweight === true) reasons.push('lightweight');
      console.log(`[cache] non-displayable for "${projectName}" (${reasons.join(',')}) — triggering full analysis`);
      return null;
    }

    // ── SOFT degrade (cache loadable, missing newer features) ──────────────
    //    complianceRisk / news_signals missing → don't bypass. Return data
    //    with _missingFeatures so UI can render with a "click 🔄 for latest
    //    features" banner. This kills the "查看显示但点了又重跑" UX where
    //    a perfectly viewable cache silently triggered full re-analysis just
    //    because A3 compliance shipped after the cache was written.
    //
    //    The previous retry-cap pattern was unnecessary complexity hiding the
    //    real design flaw: bypass was wrong, not the retry count.
    const _missingFeatures = detectMissingFeatures(data.raw_data, data.analysis);
    if (_missingFeatures.length > 0) {
      console.log(`[cache] "${projectName}" loadable but missing features: ${_missingFeatures.join(',')} — serving cache + flag`);
    }

    // ── Self-healing read-side coerce ──────────────────────────────
    //    Cached analyses written BEFORE Q8 (2026-06-19) may have missing
    //    risks/strengths/catalysts (DeepSeek schema末段漏字段). Same
    //    placeholder injection as fresh path, so UI never shows empty cards.
    if (data.analysis) {
      const phZh = '⚠️ AI 本次未生成此项 — 请点 🔄 强刷重试，或反馈给我们';
      const phEn = '⚠️ Not generated this run — click 🔄 to refresh or report to us';
      for (const k of ['risks', 'strengths', 'catalysts']) {
        if (!Array.isArray(data.analysis[k]) || data.analysis[k].length === 0) {
          data.analysis[k] = [phZh];
        }
        const enK = `${k}_en`;
        if (!Array.isArray(data.analysis[enK]) || data.analysis[enK].length === 0) {
          data.analysis[enK] = [phEn];
        }
      }
      // Q25：array-shaped 字段防御（comparable_projects / fdv_tiers / unlock_schedule
      // / exit_paths*）。老缓存若被写成 object，UI 端 .map() 会崩成白页。
      for (const k of ['comparable_projects', 'fdv_tiers', 'unlock_schedule', 'exit_paths', 'exit_paths_en']) {
        const v = data.analysis[k];
        if (v != null && !Array.isArray(v)) {
          data.analysis[k] = typeof v === 'object' ? Object.values(v).filter(x => x != null) : [];
        }
      }
    }

    // Attach _missingFeatures so AnalyzerPage can render a "missing newer
    // features" banner without re-checking schema. Empty array means cache is
    // fully feature-current.
    return { ...data, _missingFeatures };
  } catch {
    return null;
  }
}

/**
 * 阶段化 TTL：TGE后数据变化快用短缓存，早期项目数据稳定用长缓存
 */
export function getTTLHours(analysis) {
  const stage = analysis?.stage_key;
  if (stage === 'tge_post') return 6;   // 已上线：价格/解锁每小时变
  if (stage === 'growth')  return 12;   // 成长期：12h
  return 72;                            // 早期/未知：3天
}

/**
 * Write analysis result to cache.
 * Also appends a row to score_history (append-only, weight-agnostic raw scores).
 * Non-fatal on failure (network / RLS / schema errors are swallowed).
 */
export async function writeCache(projectName, rootdataId, analysis, rawData) {
  if (!supabase || !projectName || !analysis) return;

  // 失败/未评分的分析结果不写入缓存——没有 composite 分数的行对任何读它的地方都没有
  // 价值，只会让"没分析成功"看起来像"分析过但结果是空的"，还会在没有 upsert 之前
  // 无限累积（真实撞过：project_cache 里有项目因为反复分析失败堆了 20+ 行空壳数据）。
  if (analysis.composite == null) {
    console.warn(`[cache-write] "${projectName}": skipped — no composite score (failed/incomplete analysis)`);
    return;
  }

  // ── Q49 v2 写入侧保护：current.founders=0 + prior.founders>0 → 保留 prior team
  //    背景：fetchFullProject 端的 Q49 read-time fallback 已生效，但若 cache 自己也是
  //    空（如 06-26 Hyperliquid 案例），就找不到救命数据。这里在写入侧再加一道防御：
  //    旧 cache 有团队 + 新数据没团队 → 复用旧 enhancedTeam（避免好数据被 0 覆盖）。
  try {
    const currentEmpty = !(rawData?.enhancedTeam?.founders?.length) && !(rawData?.enhancedTeam?.topContributors?.length);
    if (currentEmpty && rawData) {
      const prior = await getCached(projectName, { rootdataId }).catch(() => null);
      const priorTeam = prior?.raw_data?.enhancedTeam;
      if (priorTeam?.founders?.length > 0) {
        // 保留旧 enhancedTeam，但保留本次新拿到的 companyStats（如 Q30 LinkedIn）
        const mergedCompanyStats = rawData.enhancedTeam?.companyStats || null;
        rawData = {
          ...rawData,
          enhancedTeam: {
            ...priorTeam,
            ...(mergedCompanyStats ? { companyStats: mergedCompanyStats } : {}),
            _fromCacheWrite: true,
            _cacheSourceDate: prior?.scored_at || null,
          },
        };
        console.log(`[cache-write] "${projectName}" 团队保护：本次空 → 沿用旧 cache (${prior?.scored_at?.slice(0, 10)}, ${priorTeam.founders.length} 人)`);
      }
    }
  } catch (e) {
    console.warn('[cache-write] Q49 v2 团队保护失败:', e?.message);
  }

  // Diagnostic: log what we're actually writing so we can verify news_signals is present
  // Surfaces the death-loop class of bug where the analysis silently lacks a field that
  // the read-side bypass checks for.
  const hasNewsSignals = analysis && 'news_signals' in analysis;
  const hasCompliance = rawData && 'complianceRisk' in rawData;
  const mv = rawData?.marketVolume;
  const mvSummary = mv?.totalVolume
    ? `yes ($${(mv.totalVolume/1e6).toFixed(0)}M, ${mv.tickerCount} pairs)`
    : (mv === null ? 'NO (no tickers)' : 'MISSING');
  const defi = rawData?.defiData;
  const defiSummary = defi?.tvl?.current
    ? `tvl=$${(defi.tvl.current/1e6).toFixed(0)}M slug=${defi.protocol?.slug || '?'} type=${defi.protocol?.type || 'protocol'}`
    : (defi?.protocol ? `placeholder slug=${defi.protocol.slug} (no tvl)` : 'no defiData');
  console.log(`[cache-write] "${projectName}": news_signals=${hasNewsSignals ? 'yes' : 'NO'} complianceRisk=${hasCompliance ? 'yes' : 'NO'} marketVolume=${mvSummary} defi=${defiSummary} composite=${analysis?.composite}`);
  try {
    // 2026-07-22：写入改走代理的可信中继端点（service key + 服务端校验：schema 强制、
    // composite 重算、RootData 身份核验、历史方差检查），不再直接用 anon key 写
    // Supabase——见风险清单 §7.30-7.31 / 产品优化规划 §六十九。
    // 2026-10-11（Phase 2）：去掉匿名直写兜底，project_cache 只允许经中继端点写入。
    // 中继失败只记日志、不再绕过服务端校验直写——缓存写入失败不影响本次分析结果的展示，
    // 下次分析会重新写入。配套 migration 036 收回 anon 的 INSERT/UPDATE 策略（见风险清单 §7.26）。
    const res = await fetch(`${RD_BASE}/api/cache/write`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_name: projectName,
        rootdata_id: rootdataId != null ? String(rootdataId) : null,
        analysis,
        raw_data: rawData || null,
      }),
    }).catch(() => null);
    if (!(res && res.ok)) {
      const reason = res ? (await res.json().catch(() => ({})))?.reason : 'network_error';
      console.warn(`[cache-write] relay failed for "${projectName}" (${reason}) — cache not written`);
    }
  } catch (e) {
    console.warn(`[cache-write] threw for "${projectName}":`, e?.message);
  }

  // Append to score_history (fire-and-forget, never blocks UI)
  _writeScoreHistory(projectName, rootdataId, analysis).catch(() => {});

  // Persist projectContext (fire-and-forget; 30d TTL, see migrations/011)
  // rawData?.projectContext is set by fetchFullProject via synthesizeProjectContext
  _writeProjectContext(projectName, rootdataId, rawData?.projectContext, analysis?._model).catch(() => {});
}

/**
 * Internal: persist projectContext to project_context table (30d TTL).
 * Skip if ctx is null/empty — synthesizer returns null on AI failure.
 */
async function _writeProjectContext(projectName, rootdataId, ctx, modelUsed) {
  if (!supabase || !projectName || !ctx || typeof ctx !== 'object') return;
  // Require at least one substantive field — guard against writing empty rows
  if (!ctx.contextSummary && !ctx.productMaturity && !ctx.technicalDepth) return;

  // 2026-07-22：写入改走代理中继端点——这张表之前 anon INSERT 完全无限制、
  // 无 shape 校验，且会被 getProjectContext() 读回当作下一次真实分析的可信输入，
  // 是原始漏洞里被遗漏的一个关键疏漏，见风险清单 §7.30。
  const relayRes = await fetch(`${RD_BASE}/api/cache/write-context`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      project_name: projectName,
      rootdata_id: rootdataId != null ? String(rootdataId) : null,
      ctx,
      model_used: modelUsed || null,
    }),
  }).catch(() => null);
  if (relayRes && relayRes.ok) return;

  console.warn(`[write-context] relay failed for "${projectName}" — falling back to direct anon insert`);
  const expiresAt = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();
  await supabase.from('project_context').insert({
    project_name:         projectName,
    rootdata_id:          rootdataId != null ? String(rootdataId) : null,
    context_summary:      ctx.contextSummary      || null,
    context_summary_en:   ctx.contextSummary_en   || null,
    product_maturity:     ctx.productMaturity     || null,
    product_features:     ctx.productFeatures     || null,
    tech_stack:           ctx.techStack           || null,
    recent_momentum:      ctx.recentMomentum      || null,
    technical_depth:      ctx.technicalDepth      || null,
    competitive_position: ctx.competitivePosition || null,
    competitive_position_en: ctx.competitivePosition_en || null,
    evidence_tags:        ctx.evidenceTags        || null,
    known_risks:          ctx.knownRisks          || null,
    known_risks_en:       ctx.knownRisks_en        || null,
    sources_used:         ctx.sourcesUsed         || null,
    model_used:           modelUsed || null,
    expires_at:           expiresAt,
  });
}

/**
 * Read latest non-expired projectContext for a project.
 * Returns the same shape as synthesizeProjectContext output, or null on miss.
 * Used by fetchFullProject to skip the ~$0.02-0.05 Claude/DeepSeek call.
 */
export async function getProjectContext(projectName) {
  if (!supabase || !projectName) return null;
  try {
    const { data } = await supabase
      .from('project_context')
      .select('context_summary, context_summary_en, product_maturity, product_features, tech_stack, recent_momentum, technical_depth, competitive_position, competitive_position_en, evidence_tags, known_risks, known_risks_en, sources_used, synthesized_at')
      .ilike('project_name', safeNameForIlike(projectName))
      .gt('expires_at', new Date().toISOString())
      .order('synthesized_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!data) return null;
    return {
      contextSummary:      data.context_summary      || '',
      contextSummary_en:   data.context_summary_en   || '',
      productMaturity:     data.product_maturity     || null,
      productFeatures:     data.product_features     || [],
      techStack:           data.tech_stack           || [],
      recentMomentum:      data.recent_momentum      || null,
      technicalDepth:      data.technical_depth      || null,
      competitivePosition: data.competitive_position || '',
      competitivePosition_en: data.competitive_position_en || '',
      evidenceTags:        data.evidence_tags        || [],
      knownRisks:          data.known_risks          || [],
      knownRisks_en:       data.known_risks_en       || [],
      sourcesUsed:         data.sources_used         || [],
      generatedAt:         data.synthesized_at,
      _cached:             true,
    };
  } catch {
    return null;
  }
}

/**
 * Internal: append raw dimension scores to score_history.
 */
async function _writeScoreHistory(projectName, rootdataId, analysis) {
  if (!supabase || !projectName || !analysis?.scores) return;
  const s = analysis.scores;

  // 2026-07-22：写入改走代理中继端点（这张表之前 anon INSERT 也完全无限制）
  const relayRes = await fetch(`${RD_BASE}/api/cache/write-score-history`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      project_name: projectName,
      rootdata_id: rootdataId != null ? String(rootdataId) : null,
      scores: s,
      composite: analysis.composite ?? null,
      model_used: analysis._model ?? null,
    }),
  }).catch(() => null);
  if (relayRes && relayRes.ok) return;

  console.warn(`[write-score-history] relay failed for "${projectName}" — falling back to direct anon insert`);
  await supabase.from('score_history').insert({
    project_name: projectName,
    rootdata_id:  rootdataId != null ? String(rootdataId) : null,
    tech:         s.tech        ?? null,
    team:         s.team        ?? null,
    narrative:    s.narrative   ?? null,
    data_score:   s.data        ?? null,
    token:        s.token       ?? null,
    compliance:   s.compliance  ?? null,
    composite:    analysis.composite ?? null,
    model_used:   analysis._model    ?? null,
  });
}

/**
 * Fetch score history for a project (up to 20 entries, chronological order).
 * Returns array of { scored_at, tech, team, narrative, data_score, token, compliance, composite }
 */
export async function getScoreHistory(projectName) {
  if (!supabase || !projectName) return [];
  try {
    const { data } = await supabase
      .from('score_history')
      .select('scored_at, tech, team, narrative, data_score, token, compliance, composite')
      .ilike('project_name', safeNameForIlike(projectName))
      .order('scored_at', { ascending: true })
      .limit(20);
    return data || [];
  } catch {
    return [];
  }
}

/**
 * Batched analysis-heat lookup for list views (Pipeline/Trending cards).
 * Counts score_history rows per project — a real, non-zero-at-cold-start
 * substitute for social watchlist counts (see PrimaryLens-产品优化规划 §四十四).
 * One query for the whole visible list instead of N per-project queries.
 * @param {string[]} projectNames
 * @returns {Promise<Record<string, number>>}
 */
export async function getAnalysisHeatMap(projectNames) {
  if (!supabase || !projectNames?.length) return {};
  try {
    const { data } = await supabase
      .from('score_history')
      .select('project_name')
      .in('project_name', projectNames);
    if (!data) return {};
    const counts = {};
    data.forEach(row => {
      counts[row.project_name] = (counts[row.project_name] || 0) + 1;
    });
    return counts;
  } catch {
    return {};
  }
}

/**
 * Submit user feedback on an analysis result.
 * @param {string} projectName
 * @param {string|null} rootdataId
 * @param {{ accuracy, weakDimension?, userIntent?, comment?, composite? }} feedback
 */
export async function submitFeedback(projectName, rootdataId, feedback) {
  if (!supabase || !projectName || !feedback?.accuracy) return false;
  try {
    // 2026-07-22：写入改走代理中继端点（这张表之前 anon INSERT 也完全无限制）
    const relayRes = await fetch(`${RD_BASE}/api/cache/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_name: projectName,
        rootdata_id: rootdataId != null ? String(rootdataId) : null,
        feedback,
      }),
    }).catch(() => null);
    if (relayRes && relayRes.ok) return true;

    console.warn(`[feedback] relay failed for "${projectName}" — falling back to direct anon insert`);
    const { error } = await supabase.from('score_feedback').insert({
      project_name:          projectName,
      rootdata_id:           rootdataId != null ? String(rootdataId) : null,
      accuracy:              feedback.accuracy,
      weak_dimension:        feedback.weakDimension || null,
      user_intent:           feedback.userIntent    || null,
      comment:               feedback.comment       || null,
      composite_at_feedback: feedback.composite     ?? null,
    });
    return !error;
  } catch {
    return false;
  }
}

/**
 * Fetch editorial research notes for a project (public, no auth required).
 * Returns array of { id, title, content, source, author, tags, published_at }
 *
 * 短路：检测到 table 不存在（migration 007 没跑或 schema 被 drop），
 * 后续不再发请求，避免每次切换项目都打一发 404 到浏览器控制台。
 */
const RESEARCH_NOTES_MISSING_KEY = 'pl_research_notes_table_missing_v1';
let _researchNotesTableMissing = (() => {
  try { return localStorage.getItem(RESEARCH_NOTES_MISSING_KEY) === '1'; }
  catch { return false; }
})();
export async function getResearchNotes(projectName) {
  if (!supabase || !projectName) return [];
  if (_researchNotesTableMissing) return [];
  try {
    const { data, error } = await supabase
      .from('project_research_notes')
      .select('id, title, content, source, author, tags, published_at')
      .ilike('project_name', safeNameForIlike(projectName))
      .order('published_at', { ascending: false })
      .limit(10);
    if (error) {
      if (error.code === '42P01' || error.status === 404 || /not.*exist/i.test(error.message || '')) {
        _researchNotesTableMissing = true;
        try { localStorage.setItem(RESEARCH_NOTES_MISSING_KEY, '1'); } catch {}
        console.warn(`[research-notes] table project_research_notes not found — disabling permanently. Clear via: localStorage.removeItem('${RESEARCH_NOTES_MISSING_KEY}')`);
      }
      return [];
    }
    return data || [];
  } catch {
    return [];
  }
}

/**
 * Append a validation entry to an existing cache row.
 * Reads the current validation_log, appends, and writes back.
 * Non-fatal on failure.
 */
export async function appendValidation(projectName, entry) {
  if (!supabase || !projectName) return false;
  try {
    // 2026-07-22：写入改走代理中继端点（/api/cache/validation）。读路径不变——
    // project_cache 的 SELECT 本来就对 anon 开放，不在本次加固范围内。
    const relayRes = await fetch(`${RD_BASE}/api/cache/validation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_name: projectName, entry }),
    }).catch(() => null);
    if (relayRes && relayRes.ok) return true;
    // 2026-10-11（Phase 2）：不再回退到匿名直写（anon UPDATE 策略已收回）。
    console.warn(`[validation] relay failed for "${projectName}" — validation entry not written`);
    return false;
  } catch {
    return false;
  }
}

/**
 * Build structured_labels for knowledge-graph queries.
 * Stored as jsonb so it's queryable without parsing analysis.
 */
export function buildLabels(analysis, rawData) {
  const details = rawData?.details || {};
  const rounds  = rawData?.fundingRounds || [];
  const topVCs  = ['a16z','paradigm','binance labs','yzi labs','pantera','dragonfly','multicoin','framework'];
  const investors = (details.investors || []).map(i => (i.name || '').toLowerCase());
  return {
    track:        analysis.track             || null,
    stage_key:    analysis.stage_key         || null,
    stage_label:  analysis.stage_label       || null,
    composite:    analysis.composite         ?? null,
    scores:       analysis.scores            || null,
    tge_status:   analysis.tge_status        || null,
    unlock:       analysis.unlock_pressure   || null,
    has_token:    !!(details.token_symbol),
    ecosystem:    (details.ecosystem || []).slice(0, 6),
    tags:         (details.tags      || []).slice(0, 6),
    has_top_vc:   investors.some(n => topVCs.some(t => n.includes(t))),
    total_funding: details.total_funding     ?? null,
    lead_round:   rounds[0]?.rounds          || null,
    analyzed_date: new Date().toISOString().split('T')[0],
  };
}
